import { KV_100, KV_115, KV_138, KV_161, Voltage } from "types/voltageTypes";
import {
    GeneratorType,
    LOAD_TYPE,
    SOLAR_TYPE,
    STORAGE_TYPE,
    WIND_TYPE
} from "./generatorType";
import { getBusFiltersFromQueryParams } from "./queryParams";
import { RegionType } from "./regionType";
import {
    CAISO_PRE_SCREEN_CLUSTER_16_SCOPE_TYPE,
    DUKE_DISIS_2025_PHASE_1_SCOPE_TYPE,
    DUKE_LOAD_2032_SCOPE_TYPE,
    ERCOT_2027_SCOPE_TYPE,
    ERCOT_LOAD_2028_SCOPE_TYPE,
    getScopeFromScopes,
    ISONE_BASE_SCOPE_TYPE,
    MISO_2022_MTEP_SCOPE_TYPE,
    MISO_2022_REALISTIC_SCOPE_TYPE,
    NYISO_2029_SCOPE_TYPE,
    PJM_ALL_QUEUE_SCOPE,
    PJM_LOAD_SCOPE,
    Scope,
    ScopeName,
    SOCO_BASE_SCOPE_TYPE,
    SOCO_LOAD_SCOPE_TYPE,
    SPP_CPP_ICS_PESSIMISTIC_SCOPE_TYPE,
    SPP_LOAD_SCOPE_TYPE,
    TVA_2028_WITH_PRIORS_SCOPE_TYPE,
    WECC_LATE_STAGE_GENERATORS_SCOPE_TYPE,
    WECC_LOAD_SCOPE_TYPE
} from "./scope";

export enum ColorMode {
    CAPACITY = "capacity",
    CONSTRAINT_COUNT = "constraintCount"
}

export enum LocationFilter {
    ALL = "all",
    PIL = "pil",
    UPIL = "upil"
}

export const DEFAULT_CONSTRAINT_COUNT_THRESHOLD = {
    lowThreshold: 0,
    mediumThreshold: 5,
    highThreshold: 10
};
const DEFAULT_CAPACITY_THRESHOLD = {
    lowThreshold: 0,
    mediumThreshold: 20,
    highThreshold: 100
};
const ERCOT_CAPACITY_THRESHOLD = {
    lowThreshold: 0,
    mediumThreshold: 100,
    highThreshold: 250
};
const LOAD_CAPACITY_THRESHOLD = {
    lowThreshold: 0,
    mediumThreshold: 100,
    highThreshold: 300
};
// The backend reports this exact MW value for a deliverable bus with no
// identified constraints. It is a sentinel flag, not a real capacity, and
// must match the pipeline's _NO_CONSTRAINTS_INJECTION_THRESHOLD.
export const CAISO_UNCONSTRAINED_SENTINEL_MW = 99_999_999;
const CAISO_CAPACITY_THRESHOLD = {
    lowThreshold: 0,
    mediumThreshold: 20,
    highThreshold: 1000
};

const PJM_FILTER: BusFilters = {
    voltages: [KV_138],
    maybeMaxAllocatedCosts: {
        enabled: true,
        maxCosts: 10_000_000
    },
    maybeMaxTotalCosts: {
        enabled: false,
        maxCosts: 50_000_000
    },
    scope: PJM_ALL_QUEUE_SCOPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    capacityThresholds: DEFAULT_CAPACITY_THRESHOLD,
    owners: null
};
const PJM_LOAD_FILTER: BusFilters = {
    voltages: [KV_138],
    // Not used for load
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 10_000_000
    },
    maybeMaxTotalCosts: {
        enabled: true,
        maxCosts: 0
    },
    scope: PJM_LOAD_SCOPE,
    hideLockedBuses: false,
    includePreExistingConstraints: false,
    hideLowConfidenceBuses: false,
    capacityThresholds: LOAD_CAPACITY_THRESHOLD,
    owners: null
};

const MISO_FILTER: BusFilters = {
    voltages: [KV_138],
    maybeMaxAllocatedCosts: {
        enabled: true,
        maxCosts: 20_000_000
    },
    maybeMaxTotalCosts: {
        enabled: false,
        maxCosts: 50_000_000
    },
    scope: MISO_2022_REALISTIC_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    capacityThresholds: DEFAULT_CAPACITY_THRESHOLD,
    owners: null
};

const MISO_LOAD_FILTER: BusFilters = {
    voltages: [KV_138],
    // Not used for load
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 10_000_000
    },
    maybeMaxTotalCosts: {
        enabled: true,
        maxCosts: 0
    },
    scope: MISO_2022_MTEP_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    capacityThresholds: LOAD_CAPACITY_THRESHOLD,
    owners: null
};

const SPP_FILTER: BusFilters = {
    voltages: [KV_115],
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 20_000_000
    },
    maybeMaxTotalCosts: {
        enabled: true,
        maxCosts: 50_000_000
    },
    scope: SPP_CPP_ICS_PESSIMISTIC_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    capacityThresholds: DEFAULT_CAPACITY_THRESHOLD,
    owners: null,
    locationFilter: LocationFilter.ALL
};

const SPP_LOAD_FILTER: BusFilters = {
    voltages: [KV_115],
    // Not used for load
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 10_000_000
    },
    maybeMaxTotalCosts: {
        enabled: true,
        maxCosts: 0
    },
    scope: SPP_LOAD_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: false,
    hideLowConfidenceBuses: false,
    capacityThresholds: LOAD_CAPACITY_THRESHOLD,
    owners: null
};

const ERCOT_FILTERS: BusFilters = {
    voltages: [KV_138],
    // Filter not used in ERCOT
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 1_000_000_000
    },
    // Filter not used in ERCOT
    maybeMaxTotalCosts: {
        enabled: false,
        maxCosts: 1_000_000_000
    },
    scope: ERCOT_2027_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    capacityThresholds: ERCOT_CAPACITY_THRESHOLD,
    owners: null
};

const ERCOT_LOAD_FILTERS: BusFilters = {
    voltages: [KV_138],
    // Filter not used in ERCOT Load
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 1_000_000_000
    },
    maybeMaxTotalCosts: {
        enabled: true,
        maxCosts: 0
    },
    scope: ERCOT_LOAD_2028_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: false,
    hideLowConfidenceBuses: false,
    capacityThresholds: LOAD_CAPACITY_THRESHOLD,
    owners: null
};

const WECC_FILTERS: BusFilters = {
    voltages: [KV_115],
    maybeMaxAllocatedCosts: {
        enabled: true,
        maxCosts: 10_000_000
    },
    maybeMaxTotalCosts: {
        enabled: false,
        maxCosts: 50_000_000
    },
    scope: WECC_LATE_STAGE_GENERATORS_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    hideOutOfRegionBuses: false,
    capacityThresholds: DEFAULT_CAPACITY_THRESHOLD,
    owners: null
};

const WECC_LOAD_FILTERS: BusFilters = {
    voltages: [KV_115, KV_138],
    // Not used for load
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 10_000_000
    },
    maybeMaxTotalCosts: {
        enabled: true,
        maxCosts: 0
    },
    scope: WECC_LOAD_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: false,
    hideLowConfidenceBuses: false,
    hideOutOfRegionBuses: false,
    capacityThresholds: LOAD_CAPACITY_THRESHOLD,
    owners: null
};

const NYISO_FILTERS: BusFilters = {
    voltages: [KV_115],
    // Filter not used in NYISO
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 1_000_000_000
    },
    // Filter not used in NYISO
    maybeMaxTotalCosts: {
        enabled: false,
        maxCosts: 1_000_000_000
    },
    scope: NYISO_2029_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    capacityThresholds: DEFAULT_CAPACITY_THRESHOLD,
    owners: null
};

const TVA_FILTER: BusFilters = {
    voltages: [KV_161],
    maybeMaxAllocatedCosts: {
        enabled: true,
        maxCosts: 10_000_000
    },
    maybeMaxTotalCosts: {
        enabled: false,
        maxCosts: 50_000_000
    },
    scope: TVA_2028_WITH_PRIORS_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: false,
    hideLowConfidenceBuses: false,
    capacityThresholds: DEFAULT_CAPACITY_THRESHOLD,
    owners: null
};

const ISONE_FILTERS: BusFilters = {
    voltages: [KV_115],
    // Filter not used in ISONE
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 1_000_000_000
    },
    maybeMaxTotalCosts: {
        enabled: true,
        maxCosts: 10_000_000
    },
    scope: ISONE_BASE_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    capacityThresholds: DEFAULT_CAPACITY_THRESHOLD,
    owners: null
};

const SOCO_FILTERS: BusFilters = {
    voltages: [KV_115],
    maybeMaxAllocatedCosts: {
        enabled: true,
        maxCosts: 10_000_000
    },
    maybeMaxTotalCosts: {
        enabled: false,
        maxCosts: 50_000_000
    },
    scope: SOCO_BASE_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    capacityThresholds: DEFAULT_CAPACITY_THRESHOLD,
    owners: null
};

const SOCO_LOAD_FILTER: BusFilters = {
    voltages: [KV_115],
    // Not used for load
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 10_000_000
    },
    maybeMaxTotalCosts: {
        enabled: true,
        maxCosts: 0
    },
    scope: SOCO_LOAD_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    capacityThresholds: LOAD_CAPACITY_THRESHOLD,
    owners: null
};

const DUKE_FILTERS: BusFilters = {
    voltages: [KV_100, KV_115],
    maybeMaxAllocatedCosts: {
        enabled: true,
        maxCosts: 15_000_000
    },
    maybeMaxTotalCosts: {
        enabled: false,
        maxCosts: 50_000_000
    },
    scope: DUKE_DISIS_2025_PHASE_1_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    capacityThresholds: DEFAULT_CAPACITY_THRESHOLD,
    owners: null
};

const DUKE_LOAD_FILTER: BusFilters = {
    voltages: [KV_100, KV_115],
    // Not used for load
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 15_000_000
    },
    maybeMaxTotalCosts: {
        enabled: true,
        maxCosts: 0
    },
    scope: DUKE_LOAD_2032_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: false,
    hideLowConfidenceBuses: false,
    capacityThresholds: LOAD_CAPACITY_THRESHOLD,
    owners: null
};

const CAISO_FILTER: BusFilters = {
    voltages: [KV_115],
    maybeMaxAllocatedCosts: {
        enabled: false,
        maxCosts: 20_000_000
    },
    maybeMaxTotalCosts: {
        enabled: false,
        maxCosts: 50_000_000
    },
    scope: CAISO_PRE_SCREEN_CLUSTER_16_SCOPE_TYPE,
    hideLockedBuses: false,
    includePreExistingConstraints: true,
    hideLowConfidenceBuses: false,
    colorMode: ColorMode.CAPACITY,
    capacityThresholds: CAISO_CAPACITY_THRESHOLD,
    constraintCountThresholds: DEFAULT_CONSTRAINT_COUNT_THRESHOLD,
    owners: null
};

const REGIONS_TO_DEFAULT_FILTER: {
    readonly [key in RegionType]: {
        readonly [key in GeneratorType]: BusFilters | undefined;
    };
} = {
    NYISO: {
        [SOLAR_TYPE]: NYISO_FILTERS,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: NYISO_FILTERS,
        [LOAD_TYPE]: undefined
    },
    PJM: {
        [SOLAR_TYPE]: PJM_FILTER,
        [WIND_TYPE]: PJM_FILTER,
        [STORAGE_TYPE]: PJM_FILTER,
        [LOAD_TYPE]: PJM_LOAD_FILTER
    },
    MISO: {
        [SOLAR_TYPE]: MISO_FILTER,
        [WIND_TYPE]: MISO_FILTER,
        [STORAGE_TYPE]: MISO_FILTER,
        [LOAD_TYPE]: MISO_LOAD_FILTER
    },
    CAISO: {
        [SOLAR_TYPE]: CAISO_FILTER,
        [WIND_TYPE]: CAISO_FILTER,
        [STORAGE_TYPE]: CAISO_FILTER,
        [LOAD_TYPE]: undefined
    },
    SPP: {
        [SOLAR_TYPE]: SPP_FILTER,
        [WIND_TYPE]: SPP_FILTER,
        [STORAGE_TYPE]: undefined,
        [LOAD_TYPE]: SPP_LOAD_FILTER
    },
    ERCOT: {
        [SOLAR_TYPE]: ERCOT_FILTERS,
        [WIND_TYPE]: ERCOT_FILTERS,
        [STORAGE_TYPE]: ERCOT_FILTERS,
        [LOAD_TYPE]: ERCOT_LOAD_FILTERS
    },
    WECC: {
        [SOLAR_TYPE]: WECC_FILTERS,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: WECC_FILTERS,
        [LOAD_TYPE]: WECC_LOAD_FILTERS
    },
    TVA: {
        [SOLAR_TYPE]: TVA_FILTER,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: TVA_FILTER,
        [LOAD_TYPE]: undefined
    },
    ISONE: {
        [SOLAR_TYPE]: ISONE_FILTERS,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: ISONE_FILTERS,
        [LOAD_TYPE]: undefined
    },
    SOCO: {
        [SOLAR_TYPE]: SOCO_FILTERS,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: undefined,
        [LOAD_TYPE]: SOCO_LOAD_FILTER
    },
    DUKE: {
        [SOLAR_TYPE]: DUKE_FILTERS,
        [WIND_TYPE]: undefined,
        [STORAGE_TYPE]: DUKE_FILTERS,
        [LOAD_TYPE]: DUKE_LOAD_FILTER
    }
};

export type MaybeMaxCosts = {
    readonly enabled: boolean;
    readonly maxCosts: number;
};

export type CapacityThresholds = {
    readonly lowThreshold: number;
    readonly mediumThreshold: number;
    readonly highThreshold: number;
};

export type BusFilters = {
    readonly voltages: readonly Voltage[];
    readonly maybeMaxAllocatedCosts: MaybeMaxCosts;
    readonly maybeMaxTotalCosts: MaybeMaxCosts;
    readonly scope: ScopeName;
    readonly hideLockedBuses: boolean;
    readonly includePreExistingConstraints: boolean;
    readonly hideLowConfidenceBuses: boolean;
    // Region-specific (WECC/RTOE): hide out-of-region buses (see isOutOfRegionBus).
    // Optional like locationFilter -- only regions whose componentConfig sets
    // showOutOfRegionBusFilter surface the toggle; undefined means "show them".
    readonly hideOutOfRegionBuses?: boolean;
    readonly colorMode?: ColorMode;
    readonly capacityThresholds: CapacityThresholds;
    readonly constraintCountThresholds?: CapacityThresholds;
    readonly owners: readonly string[] | null;
    readonly locationFilter?: LocationFilter;
};

export type BusFiltersFromQueryParams = {
    readonly voltage?: Voltage;
    readonly scope?: ScopeName;
    readonly maxAllocatedCostsEnabled?: boolean;
    readonly maxAllocatedCosts?: number;
    readonly maxTotalCostsEnabled?: boolean;
    readonly maxTotalCosts?: number;
    readonly includePreExistingConstraints?: boolean;
    readonly hideLowConfidenceBuses?: boolean;
    readonly locationFilter?: LocationFilter;
};

export const getDefaultBusFilter = (
    selectedRegion: RegionType,
    selectedGeneratorType: GeneratorType,
    acceptableScopes?: ReadonlyArray<Scope>
): BusFilters => {
    const maybeBusFilters =
        REGIONS_TO_DEFAULT_FILTER[selectedRegion][selectedGeneratorType];

    if (maybeBusFilters === undefined) {
        throw Error(
            `Unexpected combination: ${selectedRegion}, ${selectedGeneratorType}`
        );
    }

    // Validate that the default scope exists and fallback to first acceptable scope if not
    if (acceptableScopes !== undefined && acceptableScopes.length > 0) {
        const foundScope = getScopeFromScopes(
            acceptableScopes,
            maybeBusFilters.scope
        );

        if (foundScope === undefined) {
            return {
                ...maybeBusFilters,
                scope: acceptableScopes[0].scope
            };
        }
    }

    return maybeBusFilters;
};

export const getDefaultBusFilterAndOverrideWithParams = (
    selectedRegion: RegionType,
    selectedGeneratorType: GeneratorType,
    acceptableScopes: ReadonlyArray<Scope>,
    queryParams: URLSearchParams
): BusFilters => {
    let busFilters = getDefaultBusFilter(
        selectedRegion,
        selectedGeneratorType,
        acceptableScopes
    );

    const busFiltersFromQueryParams = getBusFiltersFromQueryParams(
        acceptableScopes,
        queryParams
    );

    // TODO: Support multiple voltages from params
    if (busFiltersFromQueryParams.voltage !== undefined) {
        busFilters = {
            ...busFilters,
            voltages: [busFiltersFromQueryParams.voltage]
        };
    }

    if (busFiltersFromQueryParams.scope !== undefined) {
        busFilters = { ...busFilters, scope: busFiltersFromQueryParams.scope };
    }

    if (busFiltersFromQueryParams.maxAllocatedCostsEnabled !== undefined) {
        busFilters = {
            ...busFilters,
            maybeMaxAllocatedCosts: {
                ...busFilters.maybeMaxAllocatedCosts,
                enabled: busFiltersFromQueryParams.maxAllocatedCostsEnabled
            }
        };
    }

    if (busFiltersFromQueryParams.maxAllocatedCosts !== undefined) {
        busFilters = {
            ...busFilters,
            maybeMaxAllocatedCosts: {
                ...busFilters.maybeMaxAllocatedCosts,
                maxCosts: busFiltersFromQueryParams.maxAllocatedCosts
            }
        };
    }

    if (busFiltersFromQueryParams.maxTotalCostsEnabled !== undefined) {
        busFilters = {
            ...busFilters,
            maybeMaxTotalCosts: {
                ...busFilters.maybeMaxTotalCosts,
                enabled: busFiltersFromQueryParams.maxTotalCostsEnabled
            }
        };
    }

    if (busFiltersFromQueryParams.maxTotalCosts !== undefined) {
        busFilters = {
            ...busFilters,
            maybeMaxTotalCosts: {
                ...busFilters.maybeMaxTotalCosts,
                maxCosts: busFiltersFromQueryParams.maxTotalCosts
            }
        };
    }

    if (busFiltersFromQueryParams.includePreExistingConstraints !== undefined) {
        busFilters = {
            ...busFilters,
            includePreExistingConstraints:
                busFiltersFromQueryParams.includePreExistingConstraints
        };
    }

    // TODO: Support setting hideLowConfidenceBuses from query params
    if (busFiltersFromQueryParams.hideLowConfidenceBuses !== undefined) {
        busFilters = {
            ...busFilters,
            hideLowConfidenceBuses:
                busFiltersFromQueryParams.hideLowConfidenceBuses
        };
    }

    if (busFiltersFromQueryParams.locationFilter !== undefined) {
        busFilters = {
            ...busFilters,
            locationFilter: busFiltersFromQueryParams.locationFilter
        };
    }

    return busFilters;
};
